import React, { useState, useEffect } from 'react';
import { createUser } from '../services/api';

const MemberDetailsForm = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        rollNo: '', // Changed from rollNumber to rollNo
        branch: '', // Changed from department to branch
        cgpa: '',
        skillType: '',
        primaryDepartment: '',
        secondaryDepartment: '',
        previousExperience: '',
        otherSocieties: '',
        photo: '', // Base64 string
        github: '',
        linkedin: '',
        remarks: '' // Changed from bio to remarks
    });

    const [loading, setLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({
        show: false,
        message: '',
        type: '', // 'success' or 'error'
        progress: 100
    });
    const [fieldErrors, setFieldErrors] = useState({
        name: false,
        email: false,
        rollNo: false,
        branch: false,
        cgpa: false,
        skillType: false,
        primaryDepartment: false,
        secondaryDepartment: false,
        photo: false,
        github: false,
        linkedin: false
    });

    // Auto-hide snackbar after 4 seconds with progress animation
    useEffect(() => {
        if (snackbar.show) {
            // Start progress animation
            const progressInterval = setInterval(() => {
                setSnackbar(prev => {
                    if (prev.progress <= 0) {
                        clearInterval(progressInterval);
                        return { ...prev, show: false, progress: 100 };
                    }
                    return { ...prev, progress: prev.progress - 2.5 }; // Decrease by 2.5% every 100ms
                });
            }, 100);

            return () => clearInterval(progressInterval);
        }
    }, [snackbar.show]);

    const showSnackbar = (message, type) => {
        setSnackbar({
            show: true,
            message,
            type,
            progress: 100
        });
    };

    // Validation helper functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@thapar\.edu$/i;
        return emailRegex.test(email);
    };

    const validateRollNo = (rollNo) => {
        // Only numbers allowed
        const rollNoRegex = /^\d+$/;
        return rollNoRegex.test(rollNo);
    };

    const validateCGPA = (cgpaStr) => {
        const cgpa = parseFloat(cgpaStr);
        return !isNaN(cgpa) && cgpa >= 0.0 && cgpa <= 10.0;
    };

    const validateLinkedIn = (url) => {
        const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+\/?$/;
        return linkedinRegex.test(url);
    };

    const validateGitHub = (url) => {
        const githubRegex = /^https?:\/\/(www\.)?github\.com\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+\/?$/;
        return githubRegex.test(url);
    };

    const validateField = (name, value) => {
        let isValid = true;
        
        switch (name) {
            case 'name':
                isValid = value.trim().length > 0;
                break;
            case 'email':
                isValid = value.trim().length > 0 && validateEmail(value);
                break;
            case 'rollNo':
                isValid = value.trim().length > 0 && validateRollNo(value);
                break;
            case 'branch':
                isValid = value.trim().length > 0;
                break;
            case 'cgpa':
                isValid = value.trim().length > 0 && validateCGPA(value);
                break;
            case 'skillType':
                isValid = value.trim().length > 0;
                break;
            case 'primaryDepartment':
                isValid = value.trim().length > 0;
                break;
            case 'secondaryDepartment':
                isValid = value.trim().length > 0 && value !== formData.primaryDepartment;
                break;
            case 'photo':
                isValid = value.trim().length > 0;
                break;
            case 'linkedin':
                isValid = value.trim().length > 0 && validateLinkedIn(value);
                break;
            case 'github':
                isValid = value.trim().length > 0 && validateGitHub(value);
                break;
            default:
                isValid = true;
        }
        
        setFieldErrors(prev => ({
            ...prev,
            [name]: !isValid
        }));
        
        return isValid;
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
            showSnackbar('Please upload a JPG, JPEG, or PNG image', 'error');
            return;
        }

        // Limit file size to 5MB to prevent browser crashes during compression
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            showSnackbar('Image size should be less than 5MB', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Max dimensions severely restricted to save Supabase free tier storage
                const MAX_WIDTH = 300;
                const MAX_HEIGHT = 300;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Compress image to base64 with lower quality for smaller size
                const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
                const base64Data = canvas.toDataURL(mimeType, 0.6);
                
                // Wrap in SVG
                const svgString = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"><image href="${base64Data}" width="${width}" height="${height}" /></svg>`;
                const base64Svg = `data:image/svg+xml;base64,${btoa(svgString)}`;

                setFormData(prev => ({ ...prev, photo: base64Svg }));
                validateField('photo', base64Svg);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for rollNo - only allow numbers
        if (name === 'rollNo') {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
            validateField(name, numericValue);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
            validateField(name, value);
        }
        
        // Clear snackbar when user starts typing
        if (snackbar.show) {
            setSnackbar(prev => ({ ...prev, show: false, progress: 100 }));
        }
    };

    const validateForm = () => {
        let isValid = true;
        const errors = {};

        // Validate required fields
        if (!formData.name.trim()) {
            showSnackbar('Name is required', 'error');
            errors.name = true;
            isValid = false;
        }
        
        if (!formData.rollNo.trim()) {
            showSnackbar('Roll Number is required', 'error');
            errors.rollNo = true;
            isValid = false;
        } else if (!validateRollNo(formData.rollNo)) {
            showSnackbar('Roll Number must contain only numbers', 'error');
            errors.rollNo = true;
            isValid = false;
        }
        
        if (!formData.email.trim()) {
            showSnackbar('Email is required', 'error');
            errors.email = true;
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            showSnackbar('Please enter a valid @thapar.edu email address', 'error');
            errors.email = true;
            isValid = false;
        }

        if (!formData.branch.trim()) {
            showSnackbar('Branch is required', 'error');
            errors.branch = true;
            isValid = false;
        }

        if (!formData.cgpa.trim()) {
            showSnackbar('CGPA is required', 'error');
            errors.cgpa = true;
            isValid = false;
        } else if (!validateCGPA(formData.cgpa)) {
            showSnackbar('CGPA must be between 0.0 and 10.0', 'error');
            errors.cgpa = true;
            isValid = false;
        }

        if (!formData.skillType.trim()) {
            showSnackbar('Skill is required', 'error');
            errors.skillType = true;
            isValid = false;
        }

        if (!formData.primaryDepartment.trim()) {
            showSnackbar('Primary Department is required', 'error');
            errors.primaryDepartment = true;
            isValid = false;
        }

        if (!formData.secondaryDepartment.trim()) {
            showSnackbar('Secondary Department is required', 'error');
            errors.secondaryDepartment = true;
            isValid = false;
        } else if (formData.primaryDepartment === formData.secondaryDepartment) {
            showSnackbar('Primary and Secondary departments cannot be the same', 'error');
            errors.secondaryDepartment = true;
            isValid = false;
        }

        if (!formData.photo) {
            showSnackbar('Photo is required', 'error');
            errors.photo = true;
            isValid = false;
        }

        // Validate required social links
        if (!formData.linkedin.trim()) {
            showSnackbar('LinkedIn URL is required', 'error');
            errors.linkedin = true;
            isValid = false;
        } else if (!validateLinkedIn(formData.linkedin)) {
            showSnackbar('Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)', 'error');
            errors.linkedin = true;
            isValid = false;
        }

        if (!formData.github.trim()) {
            showSnackbar('GitHub URL is required', 'error');
            errors.github = true;
            isValid = false;
        } else if (!validateGitHub(formData.github)) {
            showSnackbar('Please enter a valid GitHub URL (e.g., https://github.com/username)', 'error');
            errors.github = true;
            isValid = false;
        }

        // Update field errors
        setFieldErrors(errors);
        
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);

        try {
            // Prepare member data according to new API format
            const memberData = {
                name: formData.name,
                email: formData.email,
                rollNo: formData.rollNo,
                branch: formData.branch,
                cgpa: formData.cgpa,
                skillType: formData.skillType,
                primaryDepartment: formData.primaryDepartment,
                secondaryDepartment: formData.secondaryDepartment,
                previousExperience: formData.previousExperience,
                otherSocieties: formData.otherSocieties,
                photo: formData.photo,
                github: formData.github,
                linkedin: formData.linkedin,
                remarks: formData.remarks
            };

            console.log('Submitting member data:', memberData);
            
            // // Simulate API call - replace with actual API call in your implementation
            // const result = await new Promise((resolve) => {
            //     setTimeout(() => {
            //         resolve({ success: true, message: 'Member added successfully!' });
            //     }, 1000);
            // });
            
            // console.log('Add member result:', result);

            const result = await createUser(memberData);
            console.log('Form submitted successfully', result);
            
            if (result && result.success) {
                setIsSubmitted(true);
            } else {
                showSnackbar(result?.error || 'Failed to submit form', 'error');
            }
        } catch (err) {
            console.error('Error in form submission:', err);
            showSnackbar('An error occurred while submitting the form', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            name: '',
            email: '',
            rollNo: '',
            branch: '',
            cgpa: '',
            skillType: '',
            primaryDepartment: '',
            secondaryDepartment: '',
            previousExperience: '',
            otherSocieties: '',
            photo: '',
            github: '',
            linkedin: '',
            remarks: ''
        });
        setSnackbar({ show: false, message: '', type: '', progress: 100 });
        setFieldErrors({
            name: false,
            email: false,
            rollNo: false,
            branch: false,
            skillType: false,
            primaryDepartment: false,
            secondaryDepartment: false,
            photo: false,
            github: false,
            linkedin: false
        });
    };

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
                <div className="bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700 p-12 text-center max-w-lg w-full animate-fade-in">
                    <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-4 font-amatic">Form Submitted!</h2>
                    <p className="text-gray-300 text-xl mb-8 font-roboto">
                        Welcome to Microsoft Learn Student Chapter
                    </p>
                    <button
                        onClick={handleReset}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg"
                    >
                        Submit Another Response
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 relative">
                    
                    <div className="flex items-center justify-center mb-4">
                        <img 
                            src="/images/mlsc-logo.png" 
                            alt="MLSC Logo" 
                            className="h-16 w-16 mr-4"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div>
                            <h1 className="text-4xl font-bold text-white font-amatic">
                                MLSC Form 2026-2027 BATCH
                            </h1>
                            <p className="text-gray-300 text-lg font-roboto">
                                Microsoft Learn Student Chapter - Form
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Container */}
                <div className="bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700 p-8">
                    <div className="space-y-6">
                        {/* Personal Information Section */}
                        <div className="border-b border-gray-700 pb-6">
                            <h2 className="text-xl font-bold text-white mb-4 font-amatic">Personal Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.name 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Roll Number *
                                    </label>
                                    <input
                                        type="text"
                                        name="rollNo"
                                        value={formData.rollNo}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.rollNo 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="Enter your roll number"
                                    />
                                    {fieldErrors.rollNo && (
                                        <p className="text-red-400 text-xs mt-1 font-roboto">
                                            Roll number must only contain numbers
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Thapar Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.email 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="example@thapar.edu"
                                    />
                                    {fieldErrors.email && (
                                        <p className="text-red-400 text-xs mt-1 font-roboto">
                                            Must be a valid @thapar.edu email
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Academic Information Section */}
                        <div className="border-b border-gray-700 pb-6">
                            <h2 className="text-xl font-bold text-white mb-4 font-amatic">Academic & Skill Information</h2>
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Branch *
                                    </label>
                                    <input
                                        type="text"
                                        name="branch"
                                        value={formData.branch}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.branch 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="e.g., COPC, COE, COBS, DSAI, ENC, ECE, RAI, etc..."
                                    />
                                  
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Current CGPA *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="10"
                                        name="cgpa"
                                        value={formData.cgpa}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.cgpa 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="e.g., 8.50"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Primary Skill / Domain *
                                    </label>
                                    <select
                                        name="skillType"
                                        value={formData.skillType}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto appearance-none ${
                                            fieldErrors.skillType 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                    >
                                        <option value="" disabled>Select your primary skill domain...</option>
                                        <optgroup label="Technical & Engineering">
                                            <option value="Frontend Development">Frontend Development (React, Next.js, etc.)</option>
                                            <option value="Backend Development">Backend Development (Node, Go, Python, etc.)</option>
                                            <option value="App Development">App Development (Flutter, React Native, etc.)</option>
                                            <option value="AI / Machine Learning">AI / Machine Learning</option>
                                            <option value="Cloud / DevOps">Cloud / DevOps</option>
                                            <option value="Cybersecurity">Cybersecurity</option>
                                            <option value="Game Development">Game Development</option>
                                            <option value="AR / VR Development">AR / VR Development</option>
                                            <option value="Other Technical">Other Technical Domain</option>
                                        </optgroup>
                                        <optgroup label="Design & Creative">
                                            <option value="UI/UX Design">UI/UX Design</option>
                                            <option value="Graphic Design">Graphic Design</option>
                                            <option value="Video Editing / Animation">Video Editing / Animation</option>
                                            <option value="3D Modeling">3D Modeling</option>
                                        </optgroup>
                                        <optgroup label="Management & Outreach">
                                            <option value="Event Management">Event Management</option>
                                            <option value="Marketing / PR">Marketing / Public Relations</option>
                                            <option value="Content Writing">Content Writing</option>
                                            <option value="Social Media Management">Social Media Management</option>
                                            <option value="Sponsorships / Corporate">Sponsorships / Corporate Relations</option>
                                        </optgroup>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Primary Department *
                                    </label>
                                    <select
                                        name="primaryDepartment"
                                        value={formData.primaryDepartment}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto appearance-none ${
                                            fieldErrors.primaryDepartment 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                    >
                                        <option value="" disabled>Select primary department...</option>
                                        <option value="Technical">Technical</option>
                                        <option value="Marketing">Marketing</option>
                                        <option value="Design">Design</option>
                                        <option value="Content">Content</option>
                                        <option value="Media">Media</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Secondary Department *
                                    </label>
                                    <select
                                        name="secondaryDepartment"
                                        value={formData.secondaryDepartment}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto appearance-none ${
                                            fieldErrors.secondaryDepartment 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                    >
                                        <option value="" disabled>Select secondary department...</option>
                                        <option value="Technical">Technical</option>
                                        <option value="Marketing">Marketing</option>
                                        <option value="Design">Design</option>
                                        <option value="Content">Content</option>
                                        <option value="Media">Media</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Upload Photo (.jpg / .jpeg / .png) <span className="text-red-400 text-xs ml-1 font-medium bg-red-400/10 px-2 py-0.5 rounded font-roboto">(Max 5MB)</span> *
                                    </label>
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                        onChange={handleImageUpload}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.photo 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                    />
                                    {formData.photo && (
                                        <div className="mt-2 text-sm text-green-400 font-roboto">
                                            Photo selected and processed successfully!
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Social Links Section */}
                        <div className="border-b border-gray-700 pb-6">
                            <h2 className="text-xl font-bold text-white mb-4 font-amatic">Social Links</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        LinkedIn Profile *
                                    </label>
                                    <input
                                        type="url"
                                        name="linkedin"
                                        value={formData.linkedin || ''}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.linkedin 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="https://linkedin.com/in/username"
                                    />
                                    {fieldErrors.linkedin && (
                                        <p className="text-red-400 text-xs mt-1 font-roboto">
                                            Please enter a valid LinkedIn URL
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        GitHub Profile *
                                    </label>
                                    <input
                                        type="url"
                                        name="github"
                                        value={formData.github || ''}
                                        onChange={handleChange}
                                        required
                                        className={`w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:bg-gray-700 transition-all duration-200 border font-roboto ${
                                            fieldErrors.github 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-600 focus:ring-blue-500'
                                        }`}
                                        placeholder="https://github.com/username"
                                    />
                                    {fieldErrors.github && (
                                        <p className="text-red-400 text-xs mt-1 font-roboto">
                                            Please enter a valid GitHub URL
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Additional Information Section */}
                        <div>
                            <h2 className="text-xl font-bold text-white mb-4 font-amatic">Experience & Additional Information</h2>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Previous experience of societies and clubs
                                    </label>
                                    <textarea
                                        name="previousExperience"
                                        value={formData.previousExperience || ''}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-gray-700 transition-all duration-200 border border-gray-600 resize-none font-roboto"
                                        placeholder="Detail any prior experience you have in other societies..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Which societies have you joined except MLSC, and how will you manage them with MLSC?
                                    </label>
                                    <textarea
                                        name="otherSocieties"
                                        value={formData.otherSocieties || ''}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-gray-700 transition-all duration-200 border border-gray-600 resize-none font-roboto"
                                        placeholder="List other societies and your time management plan..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 font-roboto">
                                        Anything interesting about you?
                                    </label>
                                    <textarea
                                        name="remarks"
                                        value={formData.remarks || ''}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full bg-gray-700/50 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-gray-700 transition-all duration-200 border border-gray-600 resize-none font-roboto"
                                        placeholder="Tell us about your interests, experience, and what you hope to achieve with MLSC..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4 pt-6">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="px-8 py-3 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition-all duration-200 border border-gray-600 font-roboto"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 shadow-lg font-roboto"
                            >
                                {loading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting...
                                    </div>
                                ) : (
                                    'Submit'
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-8">
                    <p className="text-gray-400 font-roboto">
                        © 2026-2027 Microsoft Learn Student Club. All rights reserved.
                    </p>
                </div>
            </div>

            {/* Enhanced Snackbar - Responsive */}
            {snackbar.show && (
                <div 
                    className={`fixed z-50 animate-slide-in-right ${
                        snackbar.type === 'success' 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-r from-red-500 to-rose-600'
                    } rounded-2xl shadow-2xl border border-white/20 overflow-hidden backdrop-blur-sm
                    
                    /* Mobile styles */
                    bottom-4 left-4 right-4 mx-auto max-w-sm
                    
                    /* Tablet and desktop styles */
                    sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-sm sm:w-full sm:mx-0`}
                >
                    {/* Animated Progress bar */}
                    <div className="h-1 bg-white/20 w-full relative overflow-hidden">
                        <div 
                            className="h-full bg-white/60 transition-all duration-100 ease-linear"
                            style={{ width: `${snackbar.progress}%` }}
                        ></div>
                    </div>
                    
                    <div className="p-3 sm:p-4">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start">
                                <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                                    snackbar.type === 'success' 
                                        ? 'bg-white/20' 
                                        : 'bg-white/20'
                                }`}>
                                    {snackbar.type === 'success' ? (
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                                <div className="ml-2 sm:ml-3 pt-0.5 flex-1 min-w-0">
                                    <h4 className="text-white font-semibold text-xs sm:text-sm font-roboto">
                                        {snackbar.type === 'success' ? 'Success!' : 'Error!'}
                                    </h4>
                                    <p className="text-white/90 text-xs sm:text-sm font-roboto mt-0.5 sm:mt-1 leading-relaxed break-words">
                                        {snackbar.message}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSnackbar(prev => ({ ...prev, show: false, progress: 100 }))}
                                className="flex-shrink-0 ml-2 sm:ml-4 text-white/60 hover:text-white transition-colors duration-200 p-1 hover:bg-white/10 rounded-lg touch-manipulation"
                            >
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MemberDetailsForm;